// What a doc board shows in this browser: which page is open on each board, and which pages are folded
// in the tree. Kept per browser, as the camera is on a canvas, so another device opens where it left off.

import { create } from "zustand";
import { childrenOf, pageTitle, treeRows } from "../../../shared/entries";
import type { Entry, ID } from "../../../shared/types";
import { readStoredJson, writeStoredJson } from "../../lib/stored";

interface DocUi {
  /** board id to the page open on it */
  open: Record<ID, ID>;
  /** pages whose pages inside are hidden in the tree */
  folded: Record<ID, true>;
}

const KEY = "cb:doc";

function load(): DocUi {
  const d = readStoredJson<Partial<DocUi>>(KEY, {});
  return { open: d.open && typeof d.open === "object" ? d.open : {}, folded: d.folded && typeof d.folded === "object" ? d.folded : {} };
}

export const useDocUi = create<DocUi>(() => load());

useDocUi.subscribe((s) => {
  writeStoredJson(KEY, s); // kept for this visit only when storage is out of reach
});

export function openPage(boardId: ID, pageId: ID) {
  useDocUi.setState((s) => ({ open: { ...s.open, [boardId]: pageId } }));
}

export function setFolded(pageId: ID, folded: boolean) {
  useDocUi.setState((s) => {
    const next = { ...s.folded };
    if (folded) next[pageId] = true;
    else delete next[pageId];
    return { folded: next };
  });
}

/** The page open on a board: the one kept for it when it is still there, else the first page. */
export function openOn(pages: Entry[], kept: ID | undefined): Entry | null {
  if (kept) {
    const p = pages.find((e) => e.id === kept);
    if (p) return p;
  }
  return childrenOf(pages, null)[0] ?? treeRows(pages)[0]?.entry ?? null;
}

/** The pages a page sits in, from the top down to it. */
export function trail(pages: Entry[], page: Entry): Entry[] {
  const byId = new Map(pages.map((e) => [e.id, e]));
  const out: Entry[] = [];
  for (let p = page.parent ? byId.get(page.parent) : undefined; p && out.length < 20; p = p.parent ? byId.get(p.parent) : undefined) out.unshift(p);
  return out;
}

export { pageTitle };

// ---------------------------------------------------------------------------
// A page opened to show what an agent changed in it (Features 3, the chat's buttons to where it made a change): its
// editor, on screen now or once it opens, brings the newest writing into view and glows it once.

let wanted: { page: ID; at: number } | null = null;
const showers = new Set<(page: ID) => void>();

/** Asks the page's editor to show the newest change in it, now or as it opens in the next few seconds. */
export function showChange(page: ID) {
  wanted = { page, at: Date.now() };
  for (const f of showers) f(page);
}

/** An editor listening for its page's ask, run once for an ask made just before it opened. */
export function onShowChange(page: ID, run: () => void): () => void {
  const go = () => {
    wanted = null;
    // after the page has drawn and anything that opened it has settled
    setTimeout(run, 200);
  };
  if (wanted?.page === page && Date.now() - wanted.at < 5000) go();
  const f = (p: ID) => p === page && go();
  showers.add(f);
  return () => void showers.delete(f);
}

/** The page whose history is open on each board (Features 7): in this tab only, never kept. */
export const useHistoryUi = create<{ open: Record<ID, ID> }>(() => ({ open: {} }));

export function openHistory(boardId: ID, pageId: ID) {
  useHistoryUi.setState((s) => ({ open: { ...s.open, [boardId]: pageId } }));
}

export function closeHistory(boardId: ID) {
  useHistoryUi.setState((s) => {
    const open = { ...s.open };
    delete open[boardId];
    return { open };
  });
}

/** Opens a page, and its history in the doc. */
export function showHistory(boardId: ID, pageId: ID) {
  openPage(boardId, pageId);
  openHistory(boardId, pageId);
}
